import { useGetAllCoursesLite, useGetAllMentors, useGetAllStudents } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Input from '@components/V4/Inputs/Input';
import InputLabel from '@components/V4/Inputs/InputLabel';
import RangePicker from '@components/V4/RangePicker/RangePicker';
import Select, { SelectOption } from '@components/V4/Select/Select';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { TriState } from '@shared/common';
import { ENROLLMENT_STAGE_STATUS_OPTIONS, STAGE_STATUS_OFFSET } from '@shared/constants';
import { getEndDateValue, getStartDateValue } from 'apps/agora/src/utils/helpers';
import { Moment } from 'moment';
import moment from 'moment';
import React from 'react';

interface UsersFilterProps {
  _id: string;
  fullName: string;
}

interface EnrollmentsTableProps {
  createdByOptions?: UsersFilterProps[];
  searchParams: any;
  setSearchParams: (data: Partial<any>) => void;
  studentSearch: string;
  mentorSearch: string;
  graduationYears: string[];
  setStudentSearch: React.Dispatch<React.SetStateAction<string>>;
  setMentorSearch: React.Dispatch<React.SetStateAction<string>>;
  debouncedStudentSearch: string;
  debouncedMentorSearch: string;
  isCompletedSessionsRangeInvalid?: boolean;
  hideEnrollmentsWithNoSessions?: TriState;
  showMentorsAsStudents?: TriState;
  showDeactivatedStudents?: TriState;
  selectedEnrollments: string[];
  setSelectedEnrollments: React.Dispatch<React.SetStateAction<string[]>>;
}

const EnrollmentsHeader = (props: EnrollmentsTableProps) => {
  const {
    searchParams,
    setSearchParams,
    studentSearch,
    mentorSearch,
    setStudentSearch,
    setMentorSearch,
    graduationYears,
    debouncedStudentSearch,
    debouncedMentorSearch,
    isCompletedSessionsRangeInvalid,
    hideEnrollmentsWithNoSessions,
    showMentorsAsStudents,
    showDeactivatedStudents,
    setSelectedEnrollments,
  } = props;

  const { data: courses } = useGetAllCoursesLite();

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents({
    search: debouncedStudentSearch,
    limit: 20,
  });

  const { data: mentors, isLoading: isMentorsLoading } = useGetAllMentors({
    search: debouncedMentorSearch,
    limit: 20,
  });

  const allOption = { label: 'ALL', value: null };

  const startDateRangePickerChangeHandler = (dates: [Moment | null, Moment | null]) => {
    const start = dates?.[0] ? getStartDateValue(dates?.[0]) : null;
    const end = dates?.[1] ? getEndDateValue(dates?.[1]) : null;

    setSearchParams({
      startDateRangeStart: start ? start.toISOString() : null,
      startDateRangeEnd: end ? end.toISOString() : null,
      page: 1,
    });

    setSelectedEnrollments([]);
  };

  const endDateRangePickerChangeHandler = (dates: [Moment | null, Moment | null]) => {
    const start = dates?.[0] ? getStartDateValue(dates?.[0]) : null;
    const end = dates?.[1] ? getEndDateValue(dates?.[1]) : null;

    setSearchParams({
      endDateRangeStart: start ? start.toISOString() : null,
      endDateRangeEnd: end ? end.toISOString() : null,
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const courseFilterChangeHandler = (courseId: string) => {
    setSearchParams({
      course: courseId,
      page: 1,
    });
    setSelectedEnrollments([]);
  };
  const stageFilterChangeHandler = (selected: string[] | null) => {
    if (!selected || selected.length === 0) {
      setSearchParams({ stage: null, status: null, page: 1 });
      return;
    }

    const stageValues: number[] = [];
    const statusValues: number[] = [];

    selected.forEach((valueStr) => {
      const value = Number(valueStr);
      if (value >= STAGE_STATUS_OFFSET) {
        stageValues.push(value - STAGE_STATUS_OFFSET);
      } else {
        statusValues.push(value);
      }
    });

    setSearchParams({
      stage: stageValues.length ? stageValues.join(',') : null,
      status: statusValues.length ? statusValues.join(',') : null,
      page: 1,
    });

    setSelectedEnrollments([]);
  };

  const urgencyFilterChangeHandler = (urgency: string | null) => {
    setSearchParams({
      ...(!!searchParams.mentor && { mentor: null }),
      urgency: urgency,
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const getStartDateRangePickerValue = (): [Moment, Moment] | null => {
    if (!searchParams.startDateRangeStart || !searchParams.startDateRangeEnd) return null;

    return [moment(searchParams.startDateRangeStart), moment(searchParams.startDateRangeEnd)];
  };

  const getEndDateRangePickerValue = (): [Moment, Moment] | null => {
    if (!searchParams.endDateRangeStart || !searchParams.endDateRangeEnd) return null;

    return [moment(searchParams.endDateRangeStart), moment(searchParams.endDateRangeEnd)];
  };

  const getCoursesOptions = () => {
    const courseOptions: SelectOption[] =
      courses?.map((course) => ({
        label: course.title,
        value: course._id,
      })) || [];

    if (!debouncedMentorSearch) {
      courseOptions.unshift(allOption);
    }

    return courseOptions;
  };

  const getMentorOptions = () => {
    const mentorOptions: SelectOption[] =
      mentors?.map((mentor) => ({
        label: mentor.fullName,
        value: mentor._id,
      })) || [];

    if (!debouncedMentorSearch) {
      mentorOptions.unshift(allOption);
    }

    return mentorOptions;
  };

  const getStudentOptions = () => {
    const studentOptions: SelectOption[] =
      students?.map((student) => ({
        label: student.fullName,
        value: student._id,
      })) || [];

    if (!debouncedStudentSearch) {
      studentOptions.unshift(allOption);
    }

    return studentOptions;
  };

  const getUrgencyOptions = () => {
    return [
      allOption,
      { label: 'Needs mentor soon', value: 'soon' },
      { label: 'Needs mentor now', value: 'now' },
    ];
  };

  let stagesValue: string[] = [];

  if (searchParams.stage) {
    stagesValue = [
      ...searchParams.stage.split(',').map((val: any) => Number(val) + STAGE_STATUS_OFFSET),
    ];
  }

  if (searchParams.status) {
    stagesValue = [
      ...stagesValue,
      ...searchParams.status.split(',').map((val: any) => Number(val)),
    ];
  }

  const checkboxChangeHandler = (value: TriState) => {
    setSearchParams({ hideEnrollmentsWithNoSessions: value, page: 1 });
    setSelectedEnrollments([]);
  };

  const showMentorsAsStudentsChangeHandler = (value: TriState) => {
    setSearchParams({ showMentorsAsStudents: value, page: 1 });
    setSelectedEnrollments([]);
  };

  const showDeactivatedStudentsChangeHandler = (value: TriState) => {
    setSearchParams({ showDeactivatedStudents: value, page: 1 });
    setSelectedEnrollments([]);
  };

  const studentFilterChangeHandler = (studentId: string) => {
    setSearchParams({ student: studentId, page: 1 });
    setSelectedEnrollments([]);
  };

  const mentorFilterChangeHandler = (mentorId: string) => {
    setSearchParams({
      ...(!!searchParams.urgency && { urgency: null }),
      mentor: mentorId,
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const handleAddGraduationYear = (year: string) => {
    setSearchParams({
      graduationYears: [...graduationYears, year].join(','),
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const handleRemoveGraduationYear = (year: string) => {
    setSearchParams({
      graduationYears: graduationYears.filter((item) => item !== year).join(','),
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const handleClearGraduationYears = () => {
    setSearchParams({
      graduationYears: null,
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const completedSessionsChangeHandler = (
    key: 'completedSessionsMin' | 'completedSessionsMax',
    value: string
  ) => {
    setSearchParams({
      [key]: value === '' ? null : value,
      page: 1,
    });
    setSelectedEnrollments([]);
  };

  const currentYear = moment().year();
  const years = Array.from({ length: 15 }, (_, i) => (currentYear + i).toString());

  return (
    <section className="flex flex-col gap-6 bg-surfaceObject px-18 pt-8 pb-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold">Enrollments</h3>
      </div>

      <div className="flex gap-4">
        <Select
          className="w-2/6 max-w-56"
          label="Course"
          value={searchParams?.course}
          options={getCoursesOptions()}
          onSelect={(value) => courseFilterChangeHandler(value as string)}
          placeholder="Search Course"
          variant="secondary"
        />

        <Select
          className="w-1/6 max-w-56"
          label="Student"
          value={searchParams?.student}
          filterOption={() => true}
          searchValue={studentSearch}
          onSearch={setStudentSearch}
          onBlur={() => setStudentSearch('')}
          onSelect={(value) => studentFilterChangeHandler(value as string)}
          options={getStudentOptions()}
          notFoundContent={
            isStudentsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Students"
          variant="secondary"
        />
        <Select
          className="w-1/6 max-w-56"
          label="Mentor"
          value={searchParams?.mentor}
          filterOption={() => true}
          searchValue={mentorSearch}
          onSearch={setMentorSearch}
          onSelect={(value) => mentorFilterChangeHandler(value as string)}
          onBlur={() => setMentorSearch('')}
          options={getMentorOptions()}
          notFoundContent={
            isMentorsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Mentor"
          variant="secondary"
        />

        <Select
          className="w-1/6 max-w-56"
          label="Statuses"
          value={stagesValue}
          options={ENROLLMENT_STAGE_STATUS_OPTIONS}
          onChange={(value) => stageFilterChangeHandler(value as string[])}
          placeholder="Select Stage"
          variant="secondary"
          selectedOptionFirst={false}
          mode="multiple"
        />

        <Select
          className="w-1/6 max-w-56"
          label="Urgency"
          value={searchParams?.urgency}
          options={getUrgencyOptions()}
          onChange={(value) => urgencyFilterChangeHandler(value as string)}
          placeholder="Select Urgency"
          variant="secondary"
          selectedOptionFirst={false}
        />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <RangePicker
          variant="secondary"
          label="Start Date"
          className="max-w-56"
          placeholder={['Range Start', 'Range End']}
          value={getStartDateRangePickerValue()}
          onChange={startDateRangePickerChangeHandler}
        />

        <RangePicker
          variant="secondary"
          label="Estimated End Date"
          className="max-w-56"
          value={getEndDateRangePickerValue()}
          placeholder={['Range Start', 'Range End']}
          onChange={endDateRangePickerChangeHandler}
        />

        <Select
          className="max-w-56"
          variant="secondary"
          label="Graduation Year"
          mode="multiple"
          value={searchParams.graduationYears ? searchParams.graduationYears.split(',') : []}
          onSelect={(value) => typeof value === 'string' && handleAddGraduationYear(value)}
          onDeselect={handleRemoveGraduationYear}
          onClear={handleClearGraduationYears}
          options={years.map((gradYear: string) => ({
            value: gradYear,
            label: gradYear,
          }))}
        />

        <div className="flex flex-col max-w-56">
          <InputLabel label="Completed Sessions" htmlFor="completedSessionsMin" />
          <div className="flex items-center gap-2">
            <Input
              type="text"
              name="completedSessionsMin"
              id="completedSessionsMin"
              label=""
              className="mt-0"
              placeholder="From"
              value={searchParams.completedSessionsMin ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  completedSessionsChangeHandler('completedSessionsMin', value);
                }
              }}
            />
            <span className="text-customGrey">-</span>
            <Input
              type="text"
              name="completedSessionsMax"
              id="completedSessionsMax"
              label=""
              className="mt-0"
              placeholder="To"
              value={searchParams.completedSessionsMax ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  completedSessionsChangeHandler('completedSessionsMax', value);
                }
              }}
            />
          </div>
          {isCompletedSessionsRangeInvalid && (
            <span className="text-xxs text-customRed mt-1 ml-2">
              Min must be less than or equal to max.
            </span>
          )}
        </div>

        <TriStateToggle
          title="Total Sessions:"
          options={[
            { label: 'None', value: 'checked' },
            { label: 'At least one', value: 'indeterminate' },
            { label: 'Both', value: 'unchecked' },
          ]}
          value={hideEnrollmentsWithNoSessions}
          onChange={checkboxChangeHandler}
        />

        <TriStateToggle
          title="Mentors As Students:"
          options={[
            { label: 'No', value: 'checked' },
            { label: 'Yes', value: 'indeterminate' },
            { label: 'All', value: 'unchecked' },
          ]}
          value={showMentorsAsStudents}
          onChange={showMentorsAsStudentsChangeHandler}
        />

        <TriStateToggle
          title="Show Deactivated Students:"
          options={[
            { label: 'No', value: 'unchecked' },
            { label: 'Yes', value: 'checked' },
            { label: 'All', value: 'indeterminate' },
          ]}
          value={showDeactivatedStudents}
          onChange={showDeactivatedStudentsChangeHandler}
        />
      </div>
    </section>
  );
};

export default EnrollmentsHeader;
